import { Button, Callout, Card, PropertyList, PropertyListItem, Tag } from '@humanforce/mango-ui-react';
import type { PayRunFanOutLegDto, PayRunFinalisationDto } from './payRunsApiTypes';
import { formatSystemValue } from './payRunsModel';

interface Props {
  finalisation: PayRunFinalisationDto;
  busyLeg?: string | null;
  error?: string | null;
  onRetry: (legType: string) => void;
  onRecover?: () => void;
  /** True only when EFT applies to this Pay Run. */
  abaAvailable?: boolean;
  abaBusy?: boolean;
  abaNotice?: { tone: 'positive' | 'critical'; title: string; message: string } | null;
  onDownloadAba?: () => void;
}

const when = (value: string): string => new Intl.DateTimeFormat('en-AU', {
  dateStyle: 'medium', timeStyle: 'short', timeZone: 'Australia/Sydney',
}).format(new Date(value));

const legTone = (status: string): 'positive' | 'critical' | 'pending' | 'neutral' => {
  if (status === 'succeeded') return 'positive';
  if (status === 'retryable_failure' || status === 'reconciliation_failure') return 'critical';
  if (status === 'processing') return 'pending';
  return 'neutral';
};

/**
 * The server only serves an ABA once the EFT leg is both succeeded and reconciled, so a leg that is still
 * processing or has failed must not advertise a download that is guaranteed to be refused.
 */
export const isEftLegAbaReady = (leg: PayRunFanOutLegDto): boolean =>
  leg.legType === 'eft' && leg.status === 'succeeded' && leg.reconciled;

export const PayRunFinalisationPanel = ({
  finalisation, busyLeg, error, onRetry, onRecover, abaAvailable, abaBusy, abaNotice, onDownloadAba,
}: Props) => {
  const isAbaDownloadable = (leg: PayRunFanOutLegDto) => Boolean(abaAvailable) && isEftLegAbaReady(leg) &&
    (!finalisation.abaGeneration || finalisation.abaGeneration.status === 'succeeded');
  return (
  <section aria-label="Pay Run finalisation">
    <Card
      header={<div className="mgo-cluster mgo-gap-s"><span>Payment outputs</span><Tag label="Complete" tone="positive" /></div>}
    >
      <div className="mgo-stack mgo-gap-l" aria-label="Downstream outputs">
      <p className="mgo-text-s">
        Finalised {when(finalisation.finalisedAt)} by {finalisation.finalisedBy}. The authoritative payroll result is
        complete; downstream output failures do not undo finalisation.
      </p>
      <PropertyList>
        <PropertyListItem label="Finalisation ID">{finalisation.finalisationId}</PropertyListItem>
        <PropertyListItem label="Calculation hash">{finalisation.calculationHash}</PropertyListItem>
        <PropertyListItem label="Successor Pay Run">{finalisation.successorPayRunId}</PropertyListItem>
      </PropertyList>
      {finalisation.abaGeneration && (
        <div className="mgo-stack mgo-gap-s" aria-label="Wage ABA generation">
          <div className="mgo-cluster mgo-gap-s">
            <span className="mgo-text-m mgo-weight-strong">Wage ABA</span>
            <Tag
              label={formatSystemValue(finalisation.abaGeneration.status)}
              tone={finalisation.abaGeneration.status === 'succeeded' ? 'positive'
                : finalisation.abaGeneration.status === 'pending' ? 'pending' : 'critical'}
            />
          </div>
          <PropertyList>
            <PropertyListItem label="EFT Release Date">{finalisation.abaGeneration.eftReleaseDate}</PropertyListItem>
            <PropertyListItem label="Generation version">{finalisation.abaGeneration.generationVersion}</PropertyListItem>
            {finalisation.abaGeneration.status === 'succeeded' && (
              <PropertyListItem label="File">{finalisation.abaGeneration.fileName}</PropertyListItem>
            )}
            {finalisation.abaGeneration.status === 'succeeded' && (
              <PropertyListItem label="SHA-256">{finalisation.abaGeneration.sha256}</PropertyListItem>
            )}
          </PropertyList>
          {finalisation.abaGeneration.status === 'non_reproducible' && (
            <Callout tone="critical" title="ABA cannot be reproduced">
              Contact payroll support to investigate the frozen payment evidence. Do not import an earlier download again without confirming its bank submission status.
            </Callout>
          )}
          {finalisation.abaGeneration.status === 'retryable_failure' && (
            <Callout tone="caution" title="ABA generation needs retry">
              Retry the EFT output when the cause has been resolved.
            </Callout>
          )}
        </div>
      )}
      {error && <Callout tone="critical" title="Output retry failed">{error}</Callout>}
      {finalisation.canRecover && (
        <Callout tone="caution" title="Downstream processing can be recovered" action={<Button label="Recover processing" size="small" variant="secondary" onClick={() => onRecover?.()} />}>
          The server found pending or expired processing that can be resumed safely.
        </Callout>
      )}
      <span className="mgo-text-m mgo-weight-strong">Downstream outputs</span>
      <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-desktop-2 mgo-gap-l">
        {finalisation.legs.map((leg) => (
          <Card
            key={leg.legType}
            header={(
              <div className="mgo-split mgo-gap-s">
                <span>{leg.legType.toUpperCase()}</span>
                <div className="mgo-cluster mgo-gap-xs">
                  <Tag label={leg.legType === 'eft' ? 'Simulated EFT boundary' : 'Simulated'} tone="blue" />
                  <Tag label={formatSystemValue(leg.status)} tone={legTone(leg.status)} />
                </div>
              </div>
            )}
          >
            <div className="mgo-stack mgo-gap-m" aria-busy={busyLeg === leg.legType}>
              <PropertyList>
                <PropertyListItem label="Reconciliation">
                  <Tag label={leg.reconciled ? 'Reconciled' : 'Not reconciled'} tone={leg.reconciled ? 'positive' : 'neutral'} />
                </PropertyListItem>
                <PropertyListItem label="Attempts">{leg.attemptCount}</PropertyListItem>
                <PropertyListItem label="Expected items">{leg.expectedItemCount}</PropertyListItem>
                <PropertyListItem label="Expected total">{leg.expectedControlTotal}</PropertyListItem>
              </PropertyList>
              {leg.errorDetail && <Callout tone="critical" title="Output needs attention">{leg.errorDetail}</Callout>}
              {isAbaDownloadable(leg) && abaNotice && (
                <Callout
                  tone={abaNotice.tone === 'positive' ? 'positive' : 'critical'}
                  title={abaNotice.title}
                  testId="pay-run-aba-download-result"
                >
                  {abaNotice.message}
                </Callout>
              )}
              {(leg.canRetry || isAbaDownloadable(leg)) && (
                <div className="mgo-cluster mgo-justify-end mgo-gap-s">
                  {isAbaDownloadable(leg) && (
                    <Button
                      label="Download ABA again"
                      size="small"
                      variant="secondary"
                      loading={abaBusy}
                      disabled={abaBusy}
                      testId="pay-run-download-aba"
                      onClick={() => onDownloadAba?.()}
                    />
                  )}
                  {leg.canRetry && (
                    <Button
                      label={`Retry ${leg.legType.toUpperCase()}`}
                      size="small"
                      variant="secondary"
                      loading={busyLeg === leg.legType}
                      disabled={Boolean(busyLeg)}
                      onClick={() => onRetry(leg.legType)}
                    />
                  )}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
      <Callout
        tone={finalisation.abaGeneration?.status === 'non_reproducible' ? 'critical'
          : finalisation.payRunFinalisedEventCreatedAt ? 'positive' : 'info'}
        title={finalisation.abaGeneration?.status === 'non_reproducible' ? 'Finalisation recorded; ABA needs investigation'
          : finalisation.payRunFinalisedEventCreatedAt ? 'All six outputs reconciled' : 'Downstream processing in progress'}
      >
        {finalisation.payRunFinalisedEventCreatedAt
          ? `PayRunFinalised was recorded once at ${when(finalisation.payRunFinalisedEventCreatedAt)}. This does not confirm bank acceptance or wages paid.`
          : 'PayRunFinalised will be recorded only after all six outputs are successful and reconciled.'}
      </Callout>
      </div>
    </Card>
  </section>
  );
};
